"use client";

import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { useCallback } from "react";

/**
 * Navigation utilities to abstract routing differences between React Router and Next.js
 * This provides backward-compatible APIs for existing components
 */

// Hook to replace useNavigate from React Router
export function useNavigate() {
  const router = useRouter();

  return useCallback(
    (to: string | number, options?: { replace?: boolean; state?: any }) => {
      if (typeof to === "number") {
        // Handle history navigation
        if (to === -1) {
          router.back();
        } else if (to === 1) {
          router.forward();
        }
        return;
      }

      // Handle string navigation
      if (options?.replace) {
        router.replace(to);
      } else {
        router.push(to);
      }
    },
    [router]
  );
}

// Hook to replace useLocation from React Router
export function useLocation() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  return {
    pathname,
    search: searchParams.toString() ? `?${searchParams.toString()}` : "",
    hash: typeof window !== "undefined" ? window.location.hash : "",
    state: null, // Next.js doesn't support location state like React Router
    key: pathname, // Use pathname as key
  };
}

// Hook to replace useParams from React Router
export function useParams(): Record<string, string | undefined> {
  // This is a placeholder - in Next.js, params are passed as props to page components
  // For dynamic routes, components should use the params prop directly
  // This hook is mainly for backward compatibility in shared components
  return {};
}

// Hook to replace useSearchParams functionality
export function useUrlSearchParams() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const setSearchParams = useCallback(
    (
      params: Record<string, string> | URLSearchParams,
      options?: { replace?: boolean }
    ) => {
      const newSearchParams = new URLSearchParams(searchParams);

      if (params instanceof URLSearchParams) {
        // Replace all params
        const newParams = new URLSearchParams(params);
        const url = `${pathname}?${newParams.toString()}`;
        if (options?.replace) {
          router.replace(url);
        } else {
          router.push(url);
        }
      } else {
        // Update specific params
        Object.entries(params).forEach(([key, value]) => {
          if (value === null || value === undefined || value === "") {
            newSearchParams.delete(key);
          } else {
            newSearchParams.set(key, value);
          }
        });

        const url = newSearchParams.toString()
          ? `${pathname}?${newSearchParams.toString()}`
          : pathname;

        if (options?.replace) {
          router.replace(url);
        } else {
          router.push(url);
        }
      }
    },
    [searchParams, router, pathname]
  );

  return [searchParams, setSearchParams] as const;
}

// Utility function to build URLs with query parameters
export function buildUrl(
  path: string,
  params?: Record<string, string | number | boolean | null | undefined>
) {
  if (!params) return path;

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      searchParams.set(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `${path}?${queryString}` : path;
}

// Utility function to parse current URL parameters
export function getCurrentParams(): Record<string, string> {
  if (typeof window === "undefined") return {};

  const searchParams = new URLSearchParams(window.location.search);
  const params: Record<string, string> = {};

  searchParams.forEach((value, key) => {
    params[key] = value;
  });

  return params;
}

// Utility function to check if a path is active
export function useIsActive() {
  const pathname = usePathname();

  return useCallback(
    (path: string, exact: boolean = false) => {
      if (exact) {
        return pathname === path;
      }
      return pathname.startsWith(path);
    },
    [pathname]
  );
}

// Utility function to get the current route
export function useCurrentRoute() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  return {
    pathname,
    query: Object.fromEntries(searchParams.entries()),
    asPath:
      pathname + (searchParams.toString() ? `?${searchParams.toString()}` : ""),
  };
}

// Navigation helper functions
export const navigation = {
  push: (url: string) => {
    if (typeof window !== "undefined") {
      window.history.pushState({}, "", url);
    }
  },
  replace: (url: string) => {
    if (typeof window !== "undefined") {
      window.history.replaceState({}, "", url);
    }
  },
  back: () => {
    if (typeof window !== "undefined") {
      window.history.back();
    }
  },
  forward: () => {
    if (typeof window !== "undefined") {
      window.history.forward();
    }
  },
};

export default {
  useNavigate,
  useLocation,
  useParams,
  useUrlSearchParams,
  useIsActive,
  useCurrentRoute,
  buildUrl,
  getCurrentParams,
  navigation,
};
